import { useCallback, useMemo } from 'react';
import { ApolloError } from '@apollo/client/errors';
import { ApolloQueryResult } from '@apollo/client';
import {
  Exact,
  Maybe,
  CollectorSettingsInput,
  InstalledCollectorSettingsQuery,
  useInstalledCollectorSettingsQuery
} from 'graphql/types';

const updateCollectorSettings = (
  prev: InstalledCollectorSettingsQuery,
  {
    fetchMoreResult
  }: {
    fetchMoreResult?: InstalledCollectorSettingsQuery | undefined;
    variables?:
      | Exact<{
          input?: Maybe<CollectorSettingsInput> | undefined;
        }>
      | undefined;
  }
) => {
  if (!fetchMoreResult) return prev;
  return {
    collectorSettings: {
      items: [
        ...prev.collectorSettings!.items,
        ...fetchMoreResult.collectorSettings!.items
      ],
      cursor: fetchMoreResult.collectorSettings!.cursor
    }
  };
};

export const useInstalledCollectorSettings = (): {
  data: InstalledCollectorSettingsQuery | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: (
    variables?: any
  ) => Promise<ApolloQueryResult<InstalledCollectorSettingsQuery>>;
  loadMore: (cursor: string) => void;
} => {
  const {
    loading,
    data,
    error,
    refetch,
    fetchMore
  } = useInstalledCollectorSettingsQuery({
    variables: {
      input: {
        cursor: null
      }
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  });

  const loadMore = useCallback(
    (cursor: string) => {
      fetchMore({
        variables: {
          input: {
            cursor: cursor
          }
        },
        updateQuery: updateCollectorSettings
      });
    },
    [fetchMore]
  );

  return useMemo(
    () => ({
      loading,
      error,
      data,
      refetch,
      loadMore
    }),
    [data, error, loading, refetch, loadMore]
  );
};
